import { Extension } from '@tiptap/core'
import { Plugin, PluginKey } from '@tiptap/pm/state'
import { compressImage, type CompressImageOptions } from '@utils/imageUtils'

export interface ImagePasteHandlerOptions {
  /**
   * Maximum width for compressed images (default: 600)
   */
  maxWidth: number
  /**
   * Maximum height for compressed images (default: 600)
   */
  maxHeight: number
  /**
   * JPEG quality for non-transparent images (default: 0.8)
   */
  quality: number
}

/**
 * TipTap extension that intercepts pasted and dropped images,
 * compresses them, and inserts them as optimized base64 data URLs.
 */
export const ImagePasteHandler = Extension.create<ImagePasteHandlerOptions>({
  name: 'imagePasteHandler',

  addOptions() {
    return {
      maxWidth: 600,
      maxHeight: 600,
      quality: 0.8,
    }
  },

  addProseMirrorPlugins() {
    const { maxWidth, maxHeight, quality } = this.options

    const compressionOptions: CompressImageOptions = {
      maxWidth,
      maxHeight,
      quality,
    }

    return [
      new Plugin({
        key: new PluginKey('imagePasteHandler'),
        props: {
          handlePaste: (view, event) => {
            const items = event.clipboardData?.items
            if (!items) return false

            // Look for image files in clipboard
            const imageItems: DataTransferItem[] = []
            for (let i = 0; i < items.length; i++) {
              const item = items[i]
              if (item.type.startsWith('image/')) {
                imageItems.push(item)
              }
            }

            if (imageItems.length === 0) return false

            // Process each image
            imageItems.forEach(async (item) => {
              const file = item.getAsFile()
              if (!file) return

              try {
                // Compress the image
                const compressedDataUrl = await compressImage(file, compressionOptions)

                // Insert compressed image into editor
                const { state, dispatch } = view
                const { schema } = state

                // Try to use resizableImage node type, fall back to image
                const imageNodeType = schema.nodes.resizableImage || schema.nodes.image
                if (!imageNodeType) {
                  console.warn('No image node type found in schema')
                  return
                }

                const node = imageNodeType.create({
                  src: compressedDataUrl,
                })

                const tr = state.tr.replaceSelectionWith(node)
                dispatch(tr)
              } catch (error) {
                console.error('Failed to compress pasted image:', error)
              }
            })

            // Prevent default paste behavior for images
            return true
          },

          handleDrop: (view, event, _slice, moved) => {
            // Don't handle if it's a move within the editor
            if (moved) return false

            const files = event.dataTransfer?.files
            if (!files || files.length === 0) return false

            // Look for image files
            const imageFiles: File[] = []
            for (let i = 0; i < files.length; i++) {
              const file = files[i]
              if (file.type.startsWith('image/')) {
                imageFiles.push(file)
              }
            }

            if (imageFiles.length === 0) return false

            // Get drop position
            const coordinates = view.posAtCoords({
              left: event.clientX,
              top: event.clientY,
            })

            if (!coordinates) return false

            // Process each image
            imageFiles.forEach(async (file) => {
              try {
                // Compress the image
                const compressedDataUrl = await compressImage(file, compressionOptions)

                // Insert compressed image at drop position
                const { state, dispatch } = view
                const { schema } = state

                // Try to use resizableImage node type, fall back to image
                const imageNodeType = schema.nodes.resizableImage || schema.nodes.image
                if (!imageNodeType) {
                  console.warn('No image node type found in schema')
                  return
                }

                const node = imageNodeType.create({
                  src: compressedDataUrl,
                })

                const tr = state.tr.insert(coordinates.pos, node)
                dispatch(tr)
              } catch (error) {
                console.error('Failed to compress dropped image:', error)
              }
            })

            // Prevent default drop behavior
            return true
          },
        },
      }),
    ]
  },
})
